import React, { createContext, useContext, useEffect, useState } from 'react';
import apiClient from '../shared/lib/apiClient';

export interface Agreement {
  uuid: string;
  hashid: string;
  state: string;
  deletedAt?: string | null;
  validUntil?: string | null;
  // Add other fields as needed
}

interface AgreementsContextType {
  agreements: Agreement[];
  activeAgreement: Agreement | null;
  setActiveAgreement: (agreement: Agreement) => void;
  loading: boolean;
  error: string | null;
}

const AgreementsContext = createContext<AgreementsContextType | undefined>(undefined);

export const AgreementsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [agreements, setAgreements] = useState<Agreement[]>([]);
  const [activeAgreement, setActiveAgreementState] = useState<Agreement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const maxRetries = 3;
    const baseDelay = 500; // ms

    const fetchAgreements = async (attempt = 1) => {
      console.debug('[AgreementsProvider] fetchAgreements: localStorage before fetch', localStorage.getItem('activeAgreement'));
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.get('/agreements');
        if (cancelled) return;
        setAgreements(res.data);
        // Try to restore active agreement from localStorage
        const saved = localStorage.getItem('activeAgreement');
        console.debug('[AgreementsProvider] fetchAgreements: agreements', res.data);
        console.debug('[AgreementsProvider] fetchAgreements: saved activeAgreement uuid', saved);
        if (saved) {
          const found = res.data.find((a: Agreement) => a.uuid === saved);
          console.debug('[AgreementsProvider] fetchAgreements: found in data', found);
          if (found) setActiveAgreementState(found);
        }
        setLoading(false);
      } catch {
        if (cancelled) return;
        if (attempt < maxRetries) {
          // Exponential backoff
          setTimeout(() => {
            fetchAgreements(attempt + 1);
          }, baseDelay * Math.pow(2, attempt - 1));
        } else {
          setError('Erro ao buscar acordos');
          setLoading(false);
        }
      }
    };
    fetchAgreements();
    return () => {
      cancelled = true;
    };
  }, []); // Remove api dependency to prevent repeated calls

  const setActiveAgreement = (agreement: Agreement) => {
    console.debug('[AgreementsProvider] setActiveAgreement called', agreement);
    console.debug('[AgreementsProvider] setActiveAgreement: localStorage before', localStorage.getItem('activeAgreement'));
    setActiveAgreementState(agreement);
    localStorage.setItem('activeAgreement', agreement.uuid);
    console.debug('[AgreementsProvider] setActiveAgreement: localStorage after', localStorage.getItem('activeAgreement'));
  };

  return (
    <AgreementsContext.Provider value={{ agreements, activeAgreement, setActiveAgreement, loading, error }}>
      {children}
    </AgreementsContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export function useAgreements() {
  const ctx = useContext(AgreementsContext);
  if (!ctx) throw new Error('useAgreements deve ser usado dentro de AgreementsProvider');
  return ctx;
} 